import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen,
  ChevronRight,
  Globe,
  LifeBuoy,
  LogOut,
  Shield,
  User as UserIcon,
} from 'lucide-react';
import { sessionApi } from '@/api/endpoints/session';
import { Card, CardContent } from '@/components/ui/card';
import { useSessionStore } from '@/stores/session';
import { cn } from '@/lib/utils';

interface ExternalLink {
  href: string;
  label: string;
  description: string;
  icon: typeof BookOpen;
}

const externalLinks: ExternalLink[] = [
  {
    href: 'https://fismatek.com.br/manuais/',
    label: 'Manuais',
    description: 'Documentação e guias de uso das máquinas',
    icon: BookOpen,
  },
  {
    href: 'https://fismatek.com.br/',
    label: 'Site do Fabricante',
    description: 'Página oficial da Fismatek',
    icon: Globe,
  },
  {
    href: 'https://fismatek.com.br/suporte/',
    label: 'Suporte',
    description: 'Atendimento e canais de contato',
    icon: LifeBuoy,
  },
  {
    href: 'https://fismatek.com.br/seguros/',
    label: 'Seguros',
    description: 'Coberturas e planos disponíveis',
    icon: Shield,
  },
];

export function ProfilePage() {
  const user = useSessionStore((s) => s.user);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const logout = useMutation({
    mutationFn: () => sessionApi.logout(),
    onSuccess: () => {
      queryClient.clear();
      navigate('/login', { replace: true });
    },
  });

  return (
    <div className="mx-auto max-w-2xl space-y-5 p-4 sm:p-6">
      <Card>
        <CardContent className="flex flex-col items-center gap-3 p-6 text-center">
          <div className="flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-primary to-purple-500 shadow-md">
            <UserIcon className="h-12 w-12 text-white" />
          </div>
          <div className="space-y-0.5">
            <h1 className="text-xl font-bold">{user?.name ?? '—'}</h1>
            <p className="text-sm text-muted-foreground">{user?.email ?? '—'}</p>
            {user?.phone && <p className="text-xs text-muted-foreground">{user.phone}</p>}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <ul className="divide-y divide-border">
            {externalLinks.map((it) => (
              <li key={it.href}>
                <a
                  href={it.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex w-full items-center gap-3 px-4 py-3 hover:bg-accent"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <it.icon className="h-5 w-5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold">{it.label}</p>
                    <p className="truncate text-xs text-muted-foreground">{it.description}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </a>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      <button
        type="button"
        onClick={() => logout.mutate()}
        disabled={logout.isPending}
        className={cn(
          'flex w-full items-center justify-center gap-2 rounded-xl bg-destructive px-4 py-3.5',
          'text-base font-semibold text-destructive-foreground shadow-sm transition-colors',
          'hover:bg-destructive/90 disabled:opacity-50',
        )}
      >
        <LogOut className="h-5 w-5" />
        {logout.isPending ? 'Saindo…' : 'Sair'}
      </button>
    </div>
  );
}
